import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BalanceService, ErrorStateService, VoucherRedeemModel, VoucherService, config } from '@fgb/core';
export type VoucherStatus = 'success' | 'invalid' | 'expired' | 'used' | 'empty' | null;

type StatusVariant = 'success' | 'invalid' | 'neutral';

interface StatusConfig {
  message: string;
  icon: string;
  variant: StatusVariant;
}

const STATUS_CONFIG: Record<NonNullable<VoucherStatus>, StatusConfig> = {
  success: { message: 'Applied Successfully!', icon: 'check_circle', variant: 'success' },
  invalid: { message: 'Invalid Code. Please try again', icon: 'cancel', variant: 'invalid' },
  expired: { message: 'Sorry, this code has expired', icon: 'schedule', variant: 'neutral' },
  used: { message: 'This code has already been used', icon: 'block', variant: 'neutral' },
  empty: { message: 'Voucher code is empty', icon: 'warning', variant: 'neutral' },
};

const ERROR_PATTERNS: { pattern: RegExp; status: VoucherStatus }[] = [
  { pattern: /already.*(been|redeemed)|redeemed/i, status: 'used' },
  { pattern: /expired|no longer active/i, status: 'expired' },
];

const AUTO_CLEAR_MS = 3000;

@Component({
  selector: 'fgb-vouchers',
  templateUrl: './vouchers.component.html',
  styleUrls: ['./vouchers.component.scss'],
})
export class VouchersComponent {
  private voucherService = inject(VoucherService);
  private balanceService = inject(BalanceService);
  private errorService = inject(ErrorStateService);
  private destroyRef = inject(DestroyRef);

  voucherCode = '';
  isLoading = signal(false);
  status = signal<VoucherStatus>(null);

  statusMessage = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].message : ''));
  statusIcon = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].icon : ''));
  variantType = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].variant : ''));
  isInvalid = computed(() => !!this.status() && this.status() !== 'success');

  private statusTimer: ReturnType<typeof setTimeout> | null = null;

  async redeemVoucher(): Promise<void> {
    const code = this.voucherCode.trim();

    if (!code) {
      this.setStatus('empty');
      return;
    }

    this.isLoading.set(true);
    this.status.set(null);

    const voucher = new VoucherRedeemModel();
    voucher.VoucherCode = code;

    try {
      await firstValueFrom(this.voucherService.redeemVoucher(voucher));
      await firstValueFrom(this.balanceService.fetchBalance(config.purseConfig.virtualLoyalty));
    } catch {
    } finally {
      this.isLoading.set(false);
      const errorMsg = this.errorService.errors?.[0] ?? null;

      if (!this.errorService.hasErrors() || !errorMsg) {
        this.setStatus('success');
        return;
      }

      this.setStatus(this.resolveErrorStatus(errorMsg));
    }
  }

  private resolveErrorStatus(message: string): VoucherStatus {
    for (const { pattern, status } of ERROR_PATTERNS) {
      if (pattern.test(message)) return status;
    }
    return 'invalid';
  }

  private setStatus(status: VoucherStatus): void {
    if (this.statusTimer) clearTimeout(this.statusTimer);
    this.status.set(status);
    this.statusTimer = setTimeout(() => this.status.set(null), AUTO_CLEAR_MS);
    this.destroyRef.onDestroy(() => clearTimeout(this.statusTimer!));
  }
}
